import type { Collector } from './types';
import { safe, signal, unavailable, withTimeout } from './util';

const PROBE_KEY = 'fp-check-probe';

/** Write a throwaway value, read it back, delete it. Nothing is left behind. */
function webStorageWorks(kind: 'localStorage' | 'sessionStorage'): boolean {
  try {
    const store = window[kind];
    store.setItem(PROBE_KEY, '1');
    const ok = store.getItem(PROBE_KEY) === '1';
    store.removeItem(PROBE_KEY);
    return ok;
  } catch {
    return false;
  }
}

function cookiesWork(): boolean {
  try {
    const secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${PROBE_KEY}=1; SameSite=Strict; path=/${secure}`;
    const ok = document.cookie.split('; ').includes(`${PROBE_KEY}=1`);
    document.cookie = `${PROBE_KEY}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Strict; path=/${secure}`;
    return ok;
  } catch {
    return false;
  }
}

async function indexedDbWorks(): Promise<boolean> {
  if (typeof indexedDB === 'undefined') return false;
  const opened = new Promise<boolean>((resolve) => {
    try {
      const req = indexedDB.open(PROBE_KEY);
      req.onsuccess = () => {
        req.result.close();
        indexedDB.deleteDatabase(PROBE_KEY);
        resolve(true);
      };
      req.onerror = () => resolve(false);
      req.onblocked = () => resolve(false);
    } catch {
      resolve(false);
    }
  });
  return withTimeout(opened, 1000, 'indexedDB').catch(() => false);
}

async function cacheApiWorks(): Promise<boolean> {
  if (typeof caches === 'undefined') return false;
  try {
    await withTimeout(caches.open(PROBE_KEY), 1000, 'caches.open');
    await caches.delete(PROBE_KEY);
    return true;
  } catch {
    return false;
  }
}

function formatBytes(n: number): string {
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
}

export const storageCollector: Collector = {
  id: 'storage',
  title: 'Storage',
  description: 'Which storage mechanisms work and how much space the browser offers. Test values are written, read back and deleted at once.',
  async collect() {
    if (typeof window === 'undefined') return [unavailable('storage.available', 'Storage mechanisms', 'No window')];
    return Promise.all([
      safe('storage.available', 'Storage mechanisms', async () => {
        const value = {
          localStorage: webStorageWorks('localStorage'),
          sessionStorage: webStorageWorks('sessionStorage'),
          indexedDB: await indexedDbWorks(),
          cacheApi: await cacheApiWorks(),
          cookies: cookiesWork(),
        };
        return signal('storage.available', 'Storage mechanisms', value, {
          display: Object.entries(value)
            .map(([k, ok]) => `${k}: ${ok ? 'yes' : 'no'}`)
            .join(' · '),
        });
      }),

      safe('storage.quota', 'Storage quota', async () => {
        const est = navigator.storage?.estimate;
        if (!est) return unavailable('storage.quota', 'Storage quota', 'StorageManager.estimate() not available');
        const { quota, usage } = await withTimeout(navigator.storage.estimate(), 1500, 'storage.estimate');
        if (typeof quota !== 'number') return unavailable('storage.quota', 'Storage quota', 'Quota not reported');
        return signal('storage.quota', 'Storage quota', { quota, usage: usage ?? null }, {
          display: `${formatBytes(quota)} quota (${quota.toLocaleString()} bytes) · ${formatBytes(usage ?? 0)} used`,
          note: 'Derived from your disk size (and free space in some browsers). A much smaller quota often means private browsing.',
        });
      }),

      safe('storage.persisted', 'Persistent storage granted', async () => {
        if (!navigator.storage?.persisted) return unavailable('storage.persisted', 'Persistent storage granted', 'Not supported');
        return signal('storage.persisted', 'Persistent storage granted', await withTimeout(navigator.storage.persisted(), 1000, 'persisted'));
      }),
    ]);
  },
};
